/**
 * Fixed-timestep simulation with interpolated rendering (GDD §11, allowed by §10.3).
 * The caller drives `frame` from requestAnimationFrame; the loop owns time.
 */

export interface LoopOptions {
  /** Advances the simulation by exactly one fixed step. */
  step: (dt: number) => void;
  /** Draws the scene; `alpha` in [0, 1) is how far the display time is between the last two steps. */
  render: (alpha: number) => void;
  /** Simulation rate in steps per second. */
  hz?: number;
  /** Longest wall-clock gap (s) simulated in one frame, so a stall can't snowball. */
  maxFrame?: number;
}

export interface Loop {
  frame(nowMs: number): void;
  pause(): void;
  resume(): void;
  readonly paused: boolean;
  /** Total simulated seconds. */
  readonly simTime: number;
}

export function createLoop({ step, render, hz = 60, maxFrame = 0.25 }: LoopOptions): Loop {
  const dt = 1 / hz;
  // Timing lives in one object rather than closure variables: V8 updates its number fields in place, where a
  // closure variable holding a fractional number allocates on every write (GDD §10.5).
  const s = { last: 0, started: false, acc: 0, steps: 0, paused: false };

  return {
    frame(nowMs) {
      if (!s.started || s.paused) {
        s.last = nowMs;
        s.started = true;
        render(s.acc / dt);
        return;
      }
      s.acc += Math.min((nowMs - s.last) / 1000, maxFrame);
      s.last = nowMs;
      // A tiny epsilon keeps accumulated float error from skipping a step at exact boundaries.
      while (s.acc >= dt - 1e-9) {
        step(dt);
        s.steps++;
        s.acc -= dt;
      }
      if (s.acc < 0) s.acc = 0;
      render(s.acc / dt);
    },
    pause() {
      s.paused = true;
    },
    resume() {
      s.paused = false;
      s.started = false;
    },
    get paused() {
      return s.paused;
    },
    get simTime() {
      return s.steps * dt;
    },
  };
}
